import type { CurrentUser, SessionAdapter } from '@zhiyue/core';
import { BizAppConfig } from 'config/application.manifest';
import {
  createTenant as createTenantApi,
  deleteTenant as deleteTenantApi,
  getCurrentMemberUser,
  getTenantByCode as getTenantByCodeApi,
  getTenantList as getTenantListApi,
  joinTenant as joinTenantApi,
  logout as logoutApi,
  switchTenant as switchTenantApi,
} from '@/services/bff';
import type { TenantContext } from '@/services/bff/types/auth';
import { readCurrentTenantUuid, saveCurrentTenantUuid } from './tenantStorage';
import { clearOpenPlatformToken } from '@/services/openPlatformToken';

// 缓存租户上下文的请求 Promise，防止初始化时 fetchCurrentUser 和 fetchTenantContexts 同时触发两个请求
let bootstrapTenantListPromise: Promise<TenantContext[]> | null = null;
let bootstrapTenantListSnapshot: TenantContext[] | null = null;
let bootstrapTenantListVersion = 0;
let currentSessionIdentity = '';

const normalizeTenantList = (tenantList?: TenantContext[] | null) => {
  const normalized = tenantList || [];
  const selectedTenantUuid = readCurrentTenantUuid();
  if (
    !selectedTenantUuid ||
    !normalized.some((tenant) => tenant.id === selectedTenantUuid)
  ) {
    saveCurrentTenantUuid(normalized[0]?.id);
  }
  return normalized;
};

const fetchTenantListFromApi = async () => {
  return (await getTenantListApi()) || [];
};

const clearBootstrapTenantListCache = () => {
  bootstrapTenantListVersion += 1;
  bootstrapTenantListSnapshot = null;
  bootstrapTenantListPromise = null;
};

export const getBootstrapTenantList = async () => {
  if (bootstrapTenantListSnapshot) {
    return bootstrapTenantListSnapshot;
  }

  if (!bootstrapTenantListPromise) {
    const version = bootstrapTenantListVersion;
    const pending = fetchTenantListFromApi()
      .then((tenantList) => {
        if (version !== bootstrapTenantListVersion) {
          throw new Error('登录状态已变化，请重新加载');
        }
        normalizeTenantList(tenantList);
        bootstrapTenantListSnapshot = tenantList;
        return tenantList;
      })
      .finally(() => {
        if (bootstrapTenantListPromise === pending) {
          bootstrapTenantListPromise = null;
        }
      });
    bootstrapTenantListPromise = pending;
  }

  return bootstrapTenantListPromise;
};

const consumeBootstrapTenantList = () => {
  const cached = bootstrapTenantListSnapshot;
  bootstrapTenantListSnapshot = null;
  return cached;
};

export const sessionAdapter: SessionAdapter = {
  fetchCurrentUser: async () => {
    const user = (await getCurrentMemberUser()) as CurrentUser &
      API.CurrentUser;
    const identity = [
      user.id,
      user.uuid,
      user.tenantUuid,
      user.organizationUuid,
      user.memberUuid,
    ]
      .filter(Boolean)
      .join('|');
    if (currentSessionIdentity && currentSessionIdentity !== identity) {
      clearBootstrapTenantListCache();
      saveCurrentTenantUuid(null);
    }
    if (currentSessionIdentity !== identity) {
      clearOpenPlatformToken();
    }
    currentSessionIdentity = identity;
    saveCurrentTenantUuid(user.tenantUuid);

    return user;
  },
  fetchTenants: async () => {
    const cachedTenantList = consumeBootstrapTenantList();
    if (cachedTenantList) {
      return cachedTenantList;
    }

    const result = await getBootstrapTenantList();
    consumeBootstrapTenantList();
    return result;
  },
  switchTenant: async (organizationUuid: string) => {
    const result = await switchTenantApi(organizationUuid);
    saveCurrentTenantUuid(result?.tenantUuid);
    clearOpenPlatformToken();
  },
  createTenant: async ({ tenantName }: { tenantName: string }) => {
    const result = await createTenantApi({ tenantName });
    saveCurrentTenantUuid(result?.tenantUuid);
    clearOpenPlatformToken();
    return result;
  },
  lookupTenantByCode: async (code: string) => getTenantByCodeApi(code),
  joinTenant: async (payload: { tenantUuid: string; nodeId: string }) => {
    const result = await joinTenantApi(payload);
    saveCurrentTenantUuid(result?.tenantUuid);
    clearOpenPlatformToken();
    return result;
  },
  deleteTenant: async (tenantUuid: string) => {
    const result = await deleteTenantApi(tenantUuid);
    saveCurrentTenantUuid(result?.tenantUuid || null);
    clearOpenPlatformToken();
    return result;
  },
  logout: async () => {
    const logoutBaseUrl = BizAppConfig.appSsoLogoutUrl;
    const logoutUrl = `${logoutBaseUrl}?redirect_uri=${encodeURIComponent(`${window.location.origin}/user/login`)}`;

    try {
      await logoutApi(BizAppConfig.appSsoClientId as string);
    } catch {
      // Keep local logout flow even when backend logout fails.
    } finally {
      // 登出时清除租户上下文缓存
      clearBootstrapTenantListCache();
      currentSessionIdentity = '';
      clearOpenPlatformToken();
    }

    saveCurrentTenantUuid(null);
    return { logoutUrl };
  },
};
